import React, { useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { PrimaryButton } from "../../design-system";
import { typography, textColors, interactive } from "../../design-system/tokens";
import { FormField, Input } from "../../design-system/components/inputs";
import AuthLayout from "./AuthLayout";
import { apiFetch } from "../utils/api";

/** Only allow same-origin relative return paths (e.g. /affiliate/login). */
const sanitizeReturnTo = (value) => {
  const raw = String(value || "").trim();
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useContext(AuthContext);
  const token = location.state?.recoveryToken;
  const returnTo = sanitizeReturnTo(location.state?.returnTo);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setError("");
    if (!token) {
      setError("Missing recovery token. Please restart recovery.");
      return;
    }
    if (!password || password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch("/api/auth/recovery/reset/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recovery_token: token, new_password: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        // Auto-login is handled server-side; hydrate local state
        setAuth({ userId: data.user_id, username: data.username });
        // Affiliates (and other callers) can return to their own login/portal.
        if (returnTo) navigate(returnTo);
        else navigate(`/edit/${data.username}`);
      } else {
        setError(data.error || "Failed to reset password.");
      }
    } catch (e) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="Choose a strong password to secure your account"
    >
      <h2 className={`${typography.h2} ${textColors.primary} mb-6 text-center`}>Reset Password</h2>
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-center">
          {error}
        </div>
      )}
      <div className="space-y-6">
        <FormField 
          label="New password" 
          htmlFor="password"
          required
        >
          <div className="relative">
            <Input
              id="password"
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              className="pr-16"
            />
            <button
              type="button"
              className={`absolute inset-y-0 right-0 px-3 ${typography.bodySmall} ${textColors.muted} ${textColors.hover}`}
              onClick={() => setShow((v) => !v)}
            >
              {show ? "Hide" : "Show"}
            </button>
          </div>
        </FormField>
        
        <FormField 
          label="Confirm password" 
          htmlFor="confirm"
          required
        >
          <Input
            id="confirm"
            type={show ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm new password"
          />
        </FormField>
        
        <PrimaryButton
          disabled={loading}
          onClick={submit}
          variant="gradient"
          fullWidth={true}
          isLoading={loading}
        >
          Save new password
        </PrimaryButton>
      </div>
      
      <div className="mt-6 text-center">
        <p className={`${typography.body} ${textColors.secondary}`}>
          Remember your password?{" "}
          <Link
            to={returnTo || "/edit/login"}
            className={`underline font-medium transition-colors ${interactive.link} ${interactive.linkHover}`}
          >
            Sign in
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;

